import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from '../hooks/useTranslation';
import { usePWAUpdate } from '../hooks/usePWAUpdate';
import HelpPage from './HelpPage';
import SettingsPage from './SettingsPage';
import HabitManagement from './HabitManagement';
import BadgeGallery from './BadgeGallery';
import { badgeService } from '../services/badgeService';
import '../styles/Sidebar.css';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const [showHelp, setShowHelp] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showHabitManagement, setShowHabitManagement] = useState(false);
  const [showBadgeGallery, setShowBadgeGallery] = useState(false);
  const [unlockedBadgeCount, setUnlockedBadgeCount] = useState(0);
  const { t } = useTranslation();
  const { isUpdateAvailable, isUpdating, isChecking, applyUpdate, checkForUpdate } = usePWAUpdate();
  
  useEffect(() => {
    // バッジの解除数を取得
    const badges = badgeService.getAllBadges();
    const unlocked = badges.filter(b => b.isUnlocked).length;
    setUnlockedBadgeCount(unlocked);
  }, [isOpen]); // サイドバーが開いたときに更新

  const handleNavigate = (path: string) => {
    navigate(path);
    onClose();
  };

  const handleOpenHelp = () => {
    setShowHelp(true);
    onClose();
  };

  const handleOpenSettings = () => {
    setShowSettings(true);
    onClose();
  };

  const handleOpenHabitManagement = () => {
    setShowHabitManagement(true);
    onClose();
  };

  const handleOpenBadgeGallery = () => {
    setShowBadgeGallery(true);
    onClose();
  };

  return (
    <>
      {isOpen && <div className="sidebar-overlay" onClick={onClose} />}
      <div className={`sidebar ${isOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <h2>{t('sidebar.menu')}</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        
        <div className="sidebar-content">
          <div className="menu-section">
            <button 
              className="menu-item"
              onClick={() => handleNavigate('/')}
            >
              🏠 {t('sidebar.home')}
            </button>
            
            <button 
              className="menu-item"
              onClick={handleOpenHabitManagement}
            >
              ✏️ {t('sidebar.editHabits')}
            </button>
            
            <button 
              className="menu-item badge-menu-item"
              onClick={handleOpenBadgeGallery}
            >
              <span className="menu-item-content">
                🏆 {t('sidebar.badgeCollection')}
                {unlockedBadgeCount > 0 && (
                  <span className="badge-count">{unlockedBadgeCount}</span>
                )}
              </span>
            </button>
          </div>

          <div className="menu-section">
            <button
              className="menu-item"
              onClick={handleOpenSettings}
            >
              ⚙️ {t('sidebar.settings')}
            </button>

            <button
              className="menu-item"
              onClick={handleOpenHelp}
            >
              ❓ {t('sidebar.help')}
            </button>

            {isUpdateAvailable ? (
              <button
                className="menu-item update-btn"
                onClick={applyUpdate}
                disabled={isUpdating}
              >
                🔄 {isUpdating ? t('sidebar.updating') : t('sidebar.updateAvailable')}
              </button>
            ) : (
              <button
                className="menu-item check-update-btn"
                onClick={checkForUpdate}
                disabled={isChecking}
              >
                ✅ {isChecking ? t('sidebar.checkingUpdate') : t('sidebar.checkUpdate')}
              </button>
            )}
          </div>
        </div>
        
        <div className="sidebar-footer">
          <p className="version">hibi v1.0.0</p>
        </div>
      </div>
      
      {showHelp && <HelpPage onClose={() => setShowHelp(false)} />}
      {showSettings && <SettingsPage onClose={() => setShowSettings(false)} />}
      {showHabitManagement && <HabitManagement onClose={() => setShowHabitManagement(false)} />}
      {showBadgeGallery && <BadgeGallery onClose={() => setShowBadgeGallery(false)} />}
    </>
  );
};

export default Sidebar;